import {
  PRICING_DEFAULT_PERIOD,
  type BillingPeriod,
  formatBillingPeriodLabel,
  formatPlanPrice,
  getMarketingPlanByTier,
  getPlanPrice,
} from '@/config/pricing'
import { resolveDashboardApiUrl } from '@/lib/dashboard-api'
import { type PlanTier } from '@/lib/billing'

export type PlanMetadata = {
  id: PlanTier
  name: string
  description: string
  price: string
  billingPeriod: string
  features: string[]
}

export type PlanCatalog = Record<PlanTier, PlanMetadata>

export const PLAN_ORDER: PlanTier[] = ['basic', 'professional', 'enterprise']

const buildFallbackPlan = (tier: PlanTier, period: BillingPeriod): PlanMetadata => {
  const plan = getMarketingPlanByTier(tier)
  if (!plan) {
    return {
      id: tier,
      name: tier,
      description: 'Plan disponible en Stripe.',
      price: 'Precio en Stripe',
      billingPeriod: formatBillingPeriodLabel(period),
      features: [],
    }
  }

  return {
    id: tier,
    name: plan.name,
    description: plan.description,
    price: formatPlanPrice(getPlanPrice(plan, period)),
    billingPeriod: formatBillingPeriodLabel(period),
    features: plan.features,
  }
}

export const buildFallbackCatalog = (period: BillingPeriod): PlanCatalog => ({
  basic: buildFallbackPlan('basic', period),
  professional: buildFallbackPlan('professional', period),
  enterprise: buildFallbackPlan('enterprise', period),
})

export const fetchPlanCatalog = async (options?: {
  signal?: AbortSignal
  next?: { revalidate?: number }
  period?: BillingPeriod
}): Promise<PlanCatalog> => {
  const apiBase = resolveDashboardApiUrl()
  const url = new URL(`${apiBase}/v1/billing/catalog`)
  if (options?.period) {
    url.searchParams.set('period', options.period)
  }
  const response = await fetch(url.toString(), {
    method: 'GET',
    credentials: 'include',
    signal: options?.signal,
    ...(options?.next ? { next: options.next } : {}),
  })

  if (!response.ok) {
    throw new Error(`catalog_fetch_failed_${response.status}`)
  }

  return (await response.json()) as PlanCatalog
}

export const resolvePlanMetadata = (
  plan: PlanTier,
  catalog: PlanCatalog | null,
  period: BillingPeriod = PRICING_DEFAULT_PERIOD
): PlanMetadata => catalog?.[plan] ?? buildFallbackPlan(plan, period)
